import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { CellWrapper, CellWithoutDataContent, EllipsisText } from './../styles';
import Input from '../StyledMaskedInput';


const StyledInput = styled(Input)`
  border-color: ${props => props.hasError ? '#dc2626' : '#d1d5db'};
  
  &:focus {
    border-color: ${props => props.hasError ? '#dc2626' : '#3b82f6'};
    box-shadow: 0 0 0 2px ${props => props.hasError ? 'rgba(220, 38, 38, 0.1)' : 'rgba(59, 130, 246, 0.1)'};
  }
`;

const ErrorMessage = styled.div`
  color: #dc2626;
  font-size: 0.75rem;
  margin-top: 0.25rem;
  position: absolute;
  background: white;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 10;
  white-space: nowrap;
`;

const InputContainer = styled.div`
  position: relative;
`;

DateInput.defaultProps = {
  value: '',
  editable: false,
  onChange: null,
};

DateInput.propTypes = {
  value: PropTypes.string,
  editable: PropTypes.bool,
  onChange: PropTypes.func,
};

function DateInput(props) {
  const { value: propValue, editable: propEditable, onChange, isMissing } = props;
  const [value, setValue] = useState(propValue);
  const [initialValue, setInitialValue] = useState(propValue || '');
  const [editable, setEditable] = useState(!!propEditable);
  const [validationError, setValidationError] = useState('');

  useEffect(() => {
    if (propValue !== value && !propEditable) {
      setValue(propValue);
      setInitialValue(propValue);
      setEditable(false);
      validateDate(value);
    }
  }, [propValue]);

  const validateDate = (dateValue) => {
    if (!dateValue || dateValue.trim() === '') {
      setValidationError('');
      return true;
    }

    const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;
    if (!dateRegex.test(dateValue)) {
      const error = 'Invalid date format (MM/DD/YYYY)';
      setValidationError(error);
      return false;
    }
    const [month, day, year] = dateValue.split('/').map(Number);
    const date = new Date(year, month - 1, day);

    if (date.getFullYear() !== year ||
      date.getMonth() !== month - 1 ||
      date.getDate() !== day) {
      const error = 'Invalid date';
      setValidationError(error);
      return false;
    }
    const currentYear = new Date().getFullYear();
    if (year < 1900 || year > currentYear + 10) {
      const error = `Year must be between 1900 and ${currentYear + 10}`;
      setValidationError(error);
      return false;
    }

    setValidationError('');
    return true;
  };

  const handleChange = (e) => {
    const newValue = e.target.value;
    setValue(newValue);
    let numbers = newValue.match(/\d+/g);
    if (!numbers) return;
    if (numbers.length >= 10 || newValue === '') {
      validateDate(newValue);
    }
  };

  const handleBlur = (e) => {
    const finalValue = e.target.value;
    setEditable(false);
    const isValid = validateDate(finalValue);

    if (onChange && finalValue !== initialValue) {
      onChange(finalValue, isValid);
    }
  };

  const check = () => {
    setEditable(false);
    const isValid = validateDate(value);

    if (onChange) {
      onChange(value, isValid);
    }
  };

  const edit = () => {
    setEditable(true);
  };

  return (
    <>
      {editable ? (
        <InputContainer>
          <StyledInput
            value={value}
            type="text"
            autoFocus
            onChange={handleChange}
            onPressEnter={check}
            onBlur={handleBlur}
            typeOfMask="date"
            hasError={!!validationError}
          />
          {validationError && (
            <ErrorMessage>{validationError}</ErrorMessage>
          )}
        </InputContainer>
      ) : (
        <CellWrapper>
          {value ? (
            <EllipsisText onClick={edit} style={{ color: validationError ? '#dc2626' : 'inherit' }}>
              {value}
            </EllipsisText>
          ) : (
            <CellWithoutDataContent onClick={edit} isMissing={isMissing} />
          )}
        </CellWrapper>
      )}
    </>
  );
}

export default DateInput;
